/**
 * Cached Action Assembly
 *
 * Converts recorded `CachedActionRecord[]` to storable `CachedAction[]` in two
 * passes: macro bindings (so `$RANDOM_STRING`, `$SEQUENCE`, etc. re-resolve at
 * replay) and variable parameterization (`$var.<key>` tokens so the cache
 * replays across runs with different seed values).
 *
 * @module workflows/cache/cached-action-assembly
 * @see docs/features/workflow-cache.md
 */

import type { AssertionSpec, CachedAction, CachedActionType } from '@chatsdet/types';
import type { CachedActionRecord } from './types.js';
import type { MacroResolutionLog } from './macros/types.js';

const isActionWithTextBindings = (
  actionType: string
): actionType is 'type' | 'fill_form' | 'assert' =>
  actionType === 'type' || actionType === 'fill_form' || actionType === 'assert';

const TEXT_ASSERTION_TYPES = new Set<AssertionSpec['type']>(['text_present', 'text_absent']);

const unwrapAccidentalTextAssertionQuotes = (
  value: string,
  assertionType: AssertionSpec['type'],
  field: 'condition' | 'expected'
): string => {
  if (field !== 'condition' || !TEXT_ASSERTION_TYPES.has(assertionType)) return value;
  const trimmed = value.trim();
  if (trimmed.length < 2) return value;

  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  if ((first !== '"' && first !== "'") || last !== first) return value;

  return trimmed.slice(1, -1);
};

const findMacroExpressionForValue = (
  value: string,
  macroResolutionLog: MacroResolutionLog | undefined,
  agentMacroResolutions: Map<string, string> | undefined
): string | undefined => {
  if (macroResolutionLog) {
    for (const [, { expression, resolved }] of macroResolutionLog.entries()) {
      if (value === resolved) {
        return expression;
      }
    }
  }

  return agentMacroResolutions?.get(value);
};

const buildMacroBindingsForAction = (opts: {
  action: CachedActionRecord;
  macroResolutionLog?: MacroResolutionLog;
  agentMacroResolutions?: Map<string, string>;
}): Record<string, string> | undefined => {
  const { action, macroResolutionLog, agentMacroResolutions } = opts;
  if (!isActionWithTextBindings(action.actionType)) return undefined;

  const macroBindings: Record<string, string> = {};

  const rawText = action.args?.text ?? action.args?.value;
  const textArg = typeof rawText === 'string' ? rawText : undefined;
  if (textArg) {
    const textExpression = findMacroExpressionForValue(
      textArg,
      macroResolutionLog,
      agentMacroResolutions
    );
    if (textExpression) {
      macroBindings.text = textExpression;
    }
  }

  const fields = Array.isArray(action.args?.fields)
    ? (action.args.fields as Array<{ value?: unknown }>)
    : undefined;
  if (fields) {
    for (let fi = 0; fi < fields.length; fi++) {
      const rawValue = fields[fi]?.value;
      if (typeof rawValue !== 'string' || rawValue.length === 0) continue;

      const expression = findMacroExpressionForValue(
        rawValue,
        macroResolutionLog,
        agentMacroResolutions
      );
      if (expression) {
        macroBindings[`fields.${fi}.value`] = expression;
      }
    }
  }

  const assertionCondition =
    action.actionType === 'assert' && typeof action.assertion?.condition === 'string'
      ? action.assertion.condition
      : undefined;
  if (assertionCondition) {
    const conditionExpression = findMacroExpressionForValue(
      assertionCondition,
      macroResolutionLog,
      agentMacroResolutions
    );
    if (conditionExpression) {
      macroBindings['assertion.condition'] = conditionExpression;
    }
  }

  const assertionExpected =
    action.actionType === 'assert' && typeof action.assertion?.expected === 'string'
      ? action.assertion.expected
      : undefined;
  if (assertionExpected) {
    const expectedExpression = findMacroExpressionForValue(
      assertionExpected,
      macroResolutionLog,
      agentMacroResolutions
    );
    if (expectedExpression) {
      macroBindings['assertion.expected'] = expectedExpression;
    }
  }

  return Object.keys(macroBindings).length > 0 ? macroBindings : undefined;
};

/** Match `$var.key_name` — the syntax the agent uses to reference variables */
const VAR_REF_EXACT = /^\$var\.(\w+)$/;

/**
 * Version of the cache compiler/parameterizer. Stamped into `compilationMeta`
 * on every save. Read by `invalidateStaleCompilerVersionsForWorkflow` (in the
 * workflow-node-cache repo) to sweep pre-version caches on terminal run
 * failure.
 *
 * **Bump only for correctness-level rewrites** — i.e. when the parameterizer
 * produces different output such that old caches are now semantically wrong
 * (e.g. commit 82fff150, which added navigate-URL templating so caches from
 * before it baked in concrete UUIDs). Purely additive rewrites (new capability
 * that doesn't invalidate old output) must NOT bump this, or we'll mass-clear
 * correct caches on every unrelated run failure. If per-feature gating is ever
 * needed, introduce a sibling `compilerFeatures` bitfield rather than abusing
 * the version number.
 *
 * Never decrement. Never set below 2 (v1 = pre-navigate-URL-templating).
 */
export const CACHE_COMPILER_VERSION = 3;

/**
 * Minimum length for a variable value to be eligible for substring replacement
 * inside URLs and JS expressions. UUIDs (36 chars) and generated slugs like
 * `workflow-v5hFkKrphKGw` (21 chars) clear this easily; short user-provided
 * values (e.g. "ok", "42") are held back to avoid false positives. The existing
 * 3-char exact-match threshold below is preserved for text/fill_form/assert.
 */
const SUBSTRING_MIN_VALUE_LEN = 8;

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const STRING_LITERAL = /'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/g;

/**
 * Replace any occurrence of a known variable value in a plain string with
 * `$var.<key>`. Used for navigate URLs. Longest-first so that a longer value
 * consumes its substrings before a shorter one can match them.
 */
export function replaceVariableValuesInString(
  input: string,
  sortedEntries: Array<[string, string]>
): string {
  let out = input;
  for (const [key, value] of sortedEntries) {
    if (!value || out.indexOf(value) === -1) continue;
    out = out.replace(new RegExp(escapeRegex(value), 'g'), `$var.${key}`);
  }
  return out;
}

/**
 * Replace variable values embedded inside JS string literals with tokens that
 * `resolveAssertionConditionForReplay` will expand back via `JSON.stringify`.
 *
 * Given `"Array.from(...).includes('workflow-xyz')"` and `{ workflow_name: 'workflow-xyz' }`,
 * emits `"Array.from(...).includes($var.workflow_name)"` — the bare token, so
 * replay substitutes `"workflow-xyz"` (a valid JS string literal).
 *
 * For substring occurrences inside a larger string literal, splits into
 * concatenation: `'/flows/d036830d-…'` → `'/flows/' + $var.workflow_id`.
 * At replay, this becomes `'/flows/' + "d036830d-…"` — valid JS.
 *
 * Only single- and double-quoted string literals are scanned. Template literals
 * (backticks) and non-string regions are passed through unchanged.
 *
 * Thresholds (per entry, applied inside the rewrite): an EXACT whole-literal
 * match rewrites for any value ≥ 3 chars — the same floor the type/fill_form
 * exact-value binding uses, and equally low-risk (the whole quoted literal
 * equals the captured value). SUBSTRING splits inside a larger literal stay
 * gated at {@link SUBSTRING_MIN_VALUE_LEN} to avoid false positives from
 * short values appearing inside unrelated strings. Live evidence for the
 * exact-match floor: node 8098326c minted an evaluate assert comparing
 * captured `option_three`/`option_four` where option_four's value
 * ("10PLART", 7 chars) fell below the substring threshold — its literal was
 * silently left stale, producing an assert that could never track future
 * runs' captures.
 */
export function replaceVariableValuesInJsExpression(
  js: string,
  sortedEntries: Array<[string, string]>
): string {
  if (sortedEntries.length === 0) return js;

  return js.replace(STRING_LITERAL, (literal) =>
    rewriteStringLiteralInner(literal.slice(1, -1), literal[0], sortedEntries)
  );
}

/**
 * True if the string ends with an odd number of backslashes. Re-wrapping such a
 * segment in a same-type quote would produce `…\'` or `…\"`, escaping the
 * closing quote we add and producing malformed JS. Segments with an even run
 * (including zero) re-wrap safely — `\\` is an escaped backslash, not an
 * escape of whatever follows.
 */
function endsWithOddBackslashes(s: string): boolean {
  let count = 0;
  for (let i = s.length - 1; i >= 0 && s[i] === '\\'; i--) count++;
  return count % 2 === 1;
}

function rewriteStringLiteralInner(
  inner: string,
  quote: string,
  sortedEntries: Array<[string, string]>
): string {
  for (const [key, value] of sortedEntries) {
    if (!value || inner.indexOf(value) === -1) continue;

    if (inner === value) return `$var.${key}`;

    // Substring splits are gated to long values only — a short captured value
    // appearing INSIDE a larger literal is too likely coincidental. (Exact
    // whole-literal matches above rewrite at the caller's 3-char floor.)
    if (value.length < SUBSTRING_MIN_VALUE_LEN) continue;

    const parts = inner.split(value);
    // Refuse to rewrite if any non-empty segment ends with an odd trailing
    // backslash run — the emitted `${quote}${segment}${quote}` would have its
    // closing quote escaped and produce unparseable JS. Leave this literal
    // alone for this variable and let the next entry try.
    if (parts.some((p) => p.length > 0 && endsWithOddBackslashes(p))) continue;

    const pieces: string[] = [];
    for (let i = 0; i < parts.length; i++) {
      const segment = parts[i];
      if (segment.length > 0) pieces.push(`${quote}${segment}${quote}`);
      if (i < parts.length - 1) pieces.push(`$var.${key}`);
    }
    const rebuilt = pieces.join(' + ');
    // Recurse with the remaining entries so other variable values can rewrite
    // the newly-created sub-literals.
    return replaceVariableValuesInJsExpression(
      rebuilt,
      sortedEntries.filter(([k]) => k !== key)
    );
  }
  return `${quote}${inner}${quote}`;
}

/**
 * Try to resolve a text value to a variable key. Checks:
 * 1. Literal `$var.key_name` pattern (agent typed the reference directly)
 * 2. Exact value match against known variable values (longest first)
 */
const matchVariableKey = (
  text: string,
  variableKeys: Set<string>,
  sortedEntries: Array<[string, string]>
): string | undefined => {
  // 1. Agent typed the $var. reference literally
  const refMatch = VAR_REF_EXACT.exec(text);
  if (refMatch && variableKeys.has(refMatch[1])) {
    return refMatch[1];
  }

  // 2. Exact value match
  if (text.length >= 3) {
    for (const [varKey, varValue] of sortedEntries) {
      if (text === varValue) return varKey;
    }
  }

  return undefined;
};

const buildVariableRewriteContext = (variableValues: Record<string, string>) => {
  const keys = new Set(Object.keys(variableValues));
  const sorted = Object.entries(variableValues)
    .filter(([, v]) => v.length >= 3)
    .sort(([, a], [, b]) => b.length - a.length);
  const substringEntries = sorted.filter(([, v]) => v.length >= SUBSTRING_MIN_VALUE_LEN);
  return { keys, sorted, substringEntries };
};

const rewriteAssertionStringForStorage = (opts: {
  value: string;
  field: 'condition' | 'expected';
  assertionType: AssertionSpec['type'];
  keys: Set<string>;
  sortedEntries: Array<[string, string]>;
  substringEntries: Array<[string, string]>;
  macroResolutionLog?: MacroResolutionLog;
  agentMacroResolutions?: Map<string, string>;
}): string => {
  const {
    field,
    assertionType,
    keys,
    sortedEntries,
    substringEntries,
    macroResolutionLog,
    agentMacroResolutions,
  } = opts;
  const value = unwrapAccidentalTextAssertionQuotes(opts.value, assertionType, field);

  const macroExpression = findMacroExpressionForValue(
    value,
    macroResolutionLog,
    agentMacroResolutions
  );
  if (macroExpression) {
    return macroExpression;
  }

  // Evaluate-JS conditions get the full ≥3-char entry list: exact
  // whole-literal matches rewrite at that floor, substring splits are gated
  // per-entry inside the rewriter (see replaceVariableValuesInJsExpression).
  if (
    field === 'condition' &&
    (assertionType === 'evaluate' || assertionType === 'playwright') &&
    sortedEntries.length > 0
  ) {
    return replaceVariableValuesInJsExpression(value, sortedEntries);
  }

  const exactVariableKey = matchVariableKey(value, keys, sortedEntries);
  if (exactVariableKey) {
    return `$var.${exactVariableKey}`;
  }

  if (substringEntries.length > 0) {
    return replaceVariableValuesInString(value, substringEntries);
  }

  return value;
};

export function normalizeAssertionSpecForStorage(opts: {
  assertion: AssertionSpec;
  variableValues?: Record<string, string>;
  macroResolutionLog?: MacroResolutionLog;
  agentMacroResolutions?: Map<string, string>;
}): AssertionSpec {
  const { assertion, variableValues, macroResolutionLog, agentMacroResolutions } = opts;

  const hasStringExpected = typeof assertion.expected === 'string';
  const shouldCheckMacros = macroResolutionLog !== undefined || agentMacroResolutions !== undefined;
  const shouldCheckVariables =
    variableValues !== undefined && Object.keys(variableValues).length > 0;

  if (!shouldCheckMacros && !shouldCheckVariables) {
    return assertion;
  }

  const rewriteContext = shouldCheckVariables
    ? buildVariableRewriteContext(variableValues)
    : {
        keys: new Set<string>(),
        sorted: [] as Array<[string, string]>,
        substringEntries: [] as Array<[string, string]>,
      };

  const rewrittenCondition = rewriteAssertionStringForStorage({
    value: assertion.condition,
    field: 'condition',
    assertionType: assertion.type,
    keys: rewriteContext.keys,
    sortedEntries: rewriteContext.sorted,
    substringEntries: rewriteContext.substringEntries,
    macroResolutionLog,
    agentMacroResolutions,
  });

  const rewrittenExpected = hasStringExpected
    ? rewriteAssertionStringForStorage({
        value: assertion.expected as string,
        field: 'expected',
        assertionType: assertion.type,
        keys: rewriteContext.keys,
        sortedEntries: rewriteContext.sorted,
        substringEntries: rewriteContext.substringEntries,
        macroResolutionLog,
        agentMacroResolutions,
      })
    : assertion.expected;

  if (rewrittenCondition === assertion.condition && rewrittenExpected === assertion.expected) {
    return assertion;
  }

  return {
    ...assertion,
    condition: rewrittenCondition,
    ...(hasStringExpected ? { expected: rewrittenExpected } : {}),
  };
}

/**
 * Post-hoc safety net: scan assembled CachedActions for fill/type values that
 * match known variable values (or literal `$var.` references) and assign
 * `variableKey`/`variableBindings` bindings when the live recording middleware
 * missed them.
 */
export function parameterizeActionsWithVariables(
  actions: CachedAction[],
  variableValues: Record<string, string>
): CachedAction[] {
  const { keys, sorted, substringEntries } = buildVariableRewriteContext(variableValues);

  if (keys.size === 0) return actions;

  return actions.map((action) => {
    const normalizedAssertion =
      action.actionType === 'assert' && action.assertion
        ? normalizeAssertionSpecForStorage({
            assertion: action.assertion,
            variableValues,
          })
        : action.assertion;
    const actionWithNormalizedAssertion =
      normalizedAssertion && normalizedAssertion !== action.assertion
        ? {
            ...action,
            assertion: normalizedAssertion,
          }
        : action;

    // --- type/assert: set variableKey if not already set ---
    if (
      (actionWithNormalizedAssertion.actionType === 'type' ||
        actionWithNormalizedAssertion.actionType === 'assert') &&
      !actionWithNormalizedAssertion.variableKey
    ) {
      const text =
        actionWithNormalizedAssertion.actionType === 'assert'
          ? actionWithNormalizedAssertion.assertion?.condition
          : (actionWithNormalizedAssertion.args?.text ?? actionWithNormalizedAssertion.args?.value);
      if (typeof text === 'string') {
        const varKey = matchVariableKey(text, keys, sorted);
        if (varKey) {
          return { ...actionWithNormalizedAssertion, variableKey: varKey };
        }
      }
    }

    // --- fill_form: set variableBindings for unbound fields ---
    if (
      actionWithNormalizedAssertion.actionType === 'fill_form' &&
      Array.isArray(actionWithNormalizedAssertion.args?.fields)
    ) {
      const existingBindings = actionWithNormalizedAssertion.variableBindings ?? {};
      const newBindings: Record<string, string> = { ...existingBindings };
      let changed = false;
      for (let fi = 0; fi < actionWithNormalizedAssertion.args.fields.length; fi++) {
        const bindingKey = `fields.${fi}.value`;
        if (existingBindings[bindingKey]) continue;
        const fieldValue = (
          actionWithNormalizedAssertion.args.fields as Array<{ value?: unknown }>
        )[fi]?.value;
        if (typeof fieldValue !== 'string') continue;
        const varKey = matchVariableKey(fieldValue, keys, sorted);
        if (varKey) {
          newBindings[bindingKey] = varKey;
          changed = true;
        }
      }
      if (changed) {
        return { ...actionWithNormalizedAssertion, variableBindings: newBindings };
      }
    }

    // --- navigate: rewrite substring occurrences of variable values in args.url
    //     with `$var.<key>` tokens. Replay expands via resolveUrlForReplay. ---
    if (
      actionWithNormalizedAssertion.actionType === 'navigate' &&
      typeof actionWithNormalizedAssertion.args?.url === 'string' &&
      substringEntries.length > 0
    ) {
      const rewritten = replaceVariableValuesInString(
        actionWithNormalizedAssertion.args.url,
        substringEntries
      );
      if (rewritten !== actionWithNormalizedAssertion.args.url) {
        return {
          ...actionWithNormalizedAssertion,
          args: { ...actionWithNormalizedAssertion.args, url: rewritten },
        };
      }
    }

    // --- assert with evaluate: rewrite concrete variable values embedded in
    //     the JS condition. resolveAssertionConditionForReplay expands $var.X
    //     back via JSON.stringify at replay, yielding valid JS. All stored
    //     variants of the snippet are rewritten together — the canonical
    //     `assertion.condition` (the ONLY field replay evaluates) plus the
    //     provenance copies on `args.value`/`args.condition` — so the row
    //     never carries inconsistent variants of the same predicate (live
    //     evidence: row 7fe85043 stored three divergent copies). ---
    if (
      actionWithNormalizedAssertion.actionType === 'assert' &&
      (actionWithNormalizedAssertion.assertion?.type === 'evaluate' ||
        actionWithNormalizedAssertion.assertion?.type === 'playwright') &&
      typeof actionWithNormalizedAssertion.assertion.condition === 'string' &&
      sorted.length > 0
    ) {
      const { condition } = actionWithNormalizedAssertion.assertion;
      const rewrittenCondition = replaceVariableValuesInJsExpression(condition, sorted);

      const argsValue = actionWithNormalizedAssertion.args?.value;
      const rewrittenArgsValue =
        typeof argsValue === 'string'
          ? replaceVariableValuesInJsExpression(argsValue, sorted)
          : argsValue;

      const argsCondition = actionWithNormalizedAssertion.args?.condition;
      const rewrittenArgsCondition =
        typeof argsCondition === 'string'
          ? replaceVariableValuesInJsExpression(argsCondition, sorted)
          : argsCondition;

      if (
        rewrittenCondition !== condition ||
        rewrittenArgsValue !== argsValue ||
        rewrittenArgsCondition !== argsCondition
      ) {
        const argsChanged =
          rewrittenArgsValue !== argsValue || rewrittenArgsCondition !== argsCondition;
        return {
          ...actionWithNormalizedAssertion,
          assertion: {
            ...actionWithNormalizedAssertion.assertion,
            condition: rewrittenCondition,
          },
          args: argsChanged
            ? {
                ...actionWithNormalizedAssertion.args,
                ...(typeof rewrittenArgsValue === 'string' ? { value: rewrittenArgsValue } : {}),
                ...(typeof rewrittenArgsCondition === 'string'
                  ? { condition: rewrittenArgsCondition }
                  : {}),
              }
            : actionWithNormalizedAssertion.args,
        };
      }
    }

    return actionWithNormalizedAssertion;
  });
}

/**
 * Count the substring-substitution rewrites performed by
 * `parameterizeActionsWithVariables` between an input and output action array.
 * Only covers the navigate-URL and evaluate-assert branches — the pre-existing
 * type/assert/fill_form variable-key bindings are intentionally excluded
 * because they don't mutate user-visible action content.
 *
 * Arrays are expected to be the same length and in the same order (the
 * parameterize function is a 1:1 map).
 */
export function countVariableRewrites(
  before: CachedAction[],
  after: CachedAction[]
): { navigateUrlRewrites: number; evaluateAssertRewrites: number } {
  let navigateUrlRewrites = 0;
  let evaluateAssertRewrites = 0;
  const n = Math.min(before.length, after.length);
  for (let i = 0; i < n; i++) {
    const b = before[i];
    const a = after[i];
    if (a.actionType === 'navigate' && b.args?.url !== a.args?.url) {
      navigateUrlRewrites++;
    }
    if (
      a.actionType === 'assert' &&
      (a.assertion?.type === 'evaluate' || a.assertion?.type === 'playwright') &&
      b.assertion?.condition !== a.assertion?.condition
    ) {
      evaluateAssertRewrites++;
    }
  }
  return { navigateUrlRewrites, evaluateAssertRewrites };
}

interface AssembleCachedActionsOptions {
  actions: CachedActionRecord[];
  macroResolutionLog?: MacroResolutionLog;
  agentMacroResolutions?: Map<string, string>;
}

/**
 * Convert runtime-recorded actions into persisted cache actions, preserving
 * macro source expressions through `macroBindings` when available.
 */
export function assembleCachedActionsForStorage(
  opts: AssembleCachedActionsOptions
): CachedAction[] {
  const { actions, macroResolutionLog, agentMacroResolutions } = opts;

  return actions.map((action) => {
    const macroBindings = buildMacroBindingsForAction({
      action,
      macroResolutionLog,
      agentMacroResolutions,
    });

    return {
      index: action.index,
      toolName: action.toolName,
      actionType: action.actionType as CachedActionType,
      nodeId: action.nodeId,
      selectors: action.selectors,
      args: action.args,
      ...(action.code !== undefined ? { code: action.code } : {}),
      elementDescription: action.elementDescription,
      waitBefore: action.waitBefore,
      assertion:
        action.actionType === 'assert' && action.assertion
          ? normalizeAssertionSpecForStorage({
              assertion: action.assertion,
              macroResolutionLog,
              agentMacroResolutions,
            })
          : action.assertion,
      requiresAgent: action.requiresAgent,
      selectorAuthor: action.selectorAuthor,
      isAnnotation: action.isAnnotation,
      annotationType: action.annotationType,
      annotationColor: action.annotationColor,
      ...(macroBindings && { macroBindings }),
      ...(action.variableKey && { variableKey: action.variableKey }),
      ...(action.variableBindings && { variableBindings: action.variableBindings }),
      // Layer 4 multi-tab replay: carry the recorded tab descriptor onto the
      // persisted action so replay can re-resolve the active page. Only present
      // when the action was recorded on a non-default tab (multi-tab context).
      ...(action.activeTab && { activeTab: action.activeTab }),
      ...(action.tabProvenance && { tabProvenance: action.tabProvenance }),
    };
  });
}
